import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import {
  Plus, Save, Trash2, ChevronLeft, ChevronRight, Image as ImageIcon, Loader2,
  PenLine, NotebookPen, Bold, Italic, Underline, List, FileDown, Smile, X, Maximize2, LayoutTemplate,
} from "lucide-react";

const FONTS = [
  { label: "Predeterminada", value: "" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times", value: "'Times New Roman', serif" },
  { label: "Courier", value: "'Courier New', monospace" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Comic Sans", value: "'Comic Sans MS', cursive" },
];
const FSIZES = [{ label: "Pequeño", value: "2" }, { label: "Normal", value: "3" }, { label: "Grande", value: "5" }, { label: "Enorme", value: "7" }];
const STICKER_GROUPS = [
  { name: "Logros", items: ["⭐", "🌟", "🏆", "🥇", "🎖️", "💯", "✅", "👏", "👍", "🙌", "🎯", "🔥"] },
  { name: "Escuela", items: ["📚", "✏️", "📝", "📐", "📏", "🖍️", "🖊️", "📒", "🎒", "🔬", "🧪", "🧮", "🗒️", "📎", "🌍", "🗺️", "🎓", "🧠"] },
  { name: "Naturaleza", items: ["🌈", "☀️", "🌸", "🌻", "🌵", "🌳", "🍎", "🍁", "⛅", "❄️", "🌙", "⚡", "🌊", "🍀"] },
  { name: "Animales", items: ["🐶", "🐱", "🦄", "🐢", "🦋", "🐝", "🐬", "🦁", "🐼", "🐧", "🦉", "🐸", "🐙", "🐰"] },
  { name: "Diversión", items: ["🎉", "🎨", "🚀", "🎈", "🎵", "😀", "😎", "🤩", "💡", "❤️", "💜", "🌟", "🍭", "🎪"] },
];
const LINE = 32; // altura de línea (px) — el texto y el fondo usan el mismo valor para alinear
const SHEETS = {
  lines: { label: "Líneas", css: { backgroundImage: `repeating-linear-gradient(transparent 0 ${LINE - 1}px, #cfe3f7 ${LINE - 1}px ${LINE}px)` } },
  grid: { label: "Cuadrícula", css: { backgroundImage: `repeating-linear-gradient(transparent 0 ${LINE - 1}px, #d6e6f5 ${LINE - 1}px ${LINE}px), repeating-linear-gradient(90deg, transparent 0 ${LINE - 1}px, #d6e6f5 ${LINE - 1}px ${LINE}px)` } },
  pauta: { label: "Pauta", css: { backgroundImage: `repeating-linear-gradient(transparent 0 ${LINE - 1}px, #8fa3c4 ${LINE - 1}px ${LINE}px), repeating-linear-gradient(transparent 0 ${LINE / 2 - 1}px, #e2c4ea ${LINE / 2 - 1}px ${LINE / 2}px, transparent ${LINE / 2}px ${LINE}px)` } },
  margin: { label: "Margen", css: { backgroundImage: `linear-gradient(90deg, transparent 0 6px, #f0a6a6 6px 8px, transparent 8px), repeating-linear-gradient(transparent 0 ${LINE - 1}px, #cfe3f7 ${LINE - 1}px ${LINE}px)` } },
  blank: { label: "Hoja blanca", css: { backgroundImage: "none" } },
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const uid = () => Math.random().toString(36).slice(2, 9);

export default function StudentNotebook({ token, studentName }) {
  const [pages, setPages] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [overlays, setOverlays] = useState([]);
  const [selId, setSelId] = useState(null);
  const [showStickers, setShowStickers] = useState(false);
  const [sheet, setSheet] = useState("lines");
  const sheetRef = useRef(null);
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const H = { "X-Parent-Token": token };
  const page = pages[idx];
  const exec = (cmd, val = null) => { editorRef.current?.focus(); try { document.execCommand("styleWithCSS", false, true); } catch { /* noop */ } document.execCommand(cmd, false, val); };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/student/notebook", { headers: H }); setPages(data.pages || []); setIdx(Math.max(0, (data.pages || []).length - 1)); }
    catch { setPages([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // hidratar la hoja cuando cambia la página
  useEffect(() => {
    setDate(page?.date || todayISO());
    setOverlays(Array.isArray(page?.overlays) ? page.overlays : []);
    setSheet(page?.sheet && SHEETS[page.sheet] ? page.sheet : "lines");
    setSelId(null);
    if (editorRef.current) editorRef.current.innerHTML = page?.note || "";
  }, [idx, pages]); // eslint-disable-line

  const patchOverlay = (id, patch) => setOverlays((os) => os.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const delOverlay = (id) => { setOverlays((os) => os.filter((o) => o.id !== id)); setSelId(null); };

  // Arrastre/redimensionado de un overlay dentro de la hoja
  const startDrag = (e, ov) => {
    e.stopPropagation(); e.preventDefault();
    setSelId(ov.id);
    const rect = sheetRef.current.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, ox = ov.x, oy = ov.y;
    const target = e.currentTarget;
    try { target.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const move = (ev) => {
      const dx = ((ev.clientX - sx) / rect.width) * 100;
      const dy = ((ev.clientY - sy) / rect.height) * 100;
      patchOverlay(ov.id, { x: Math.max(0, Math.min(96, ox + dx)), y: Math.max(0, Math.min(97, oy + dy)) });
    };
    const up = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* noop */ }
      target.removeEventListener("pointermove", move); target.removeEventListener("pointerup", up); target.removeEventListener("pointercancel", up);
    };
    target.addEventListener("pointermove", move); target.addEventListener("pointerup", up); target.addEventListener("pointercancel", up);
  };
  const startResize = (e, ov) => {
    e.stopPropagation(); e.preventDefault();
    setSelId(ov.id);
    const rect = sheetRef.current.getBoundingClientRect();
    const sx = e.clientX, ow = ov.w;
    const maxW = ov.kind === "sticker" ? 60 : 100;
    const target = e.currentTarget;
    try { target.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const move = (ev) => {
      const dw = ((ev.clientX - sx) / rect.width) * 100;
      patchOverlay(ov.id, { w: Math.max(4, Math.min(maxW, ow + dw)) });
    };
    const up = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* noop */ }
      target.removeEventListener("pointermove", move); target.removeEventListener("pointerup", up); target.removeEventListener("pointercancel", up);
    };
    target.addEventListener("pointermove", move); target.addEventListener("pointerup", up); target.addEventListener("pointercancel", up);
  };

  const addImage = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/student/notebook/upload", fd, { headers: { ...H, "Content-Type": "multipart/form-data" } });
      const ov = { id: uid(), kind: "image", token: data.token, name: data.name, x: 32, y: 30, w: 34 };
      setOverlays((os) => [...os, ov]); setSelId(ov.id);
      toast.success("Imagen agregada · arrástrala y ajústala");
    } catch { toast.error("No se pudo subir la imagen"); }
  };
  const addSticker = (emoji) => {
    const ov = { id: uid(), kind: "sticker", emoji, x: 42, y: 40, w: 12 };
    setOverlays((os) => [...os, ov]); setSelId(ov.id); setShowStickers(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const noteHtml = editorRef.current ? editorRef.current.innerHTML : "";
      const body = { title: `Hoja ${date}`, date, note: noteHtml, overlays, sheet };
      if (page?.id) { const { data } = await api.put(`/student/notebook/${page.id}`, body, { headers: H }); setPages((ps) => ps.map((p, i) => (i === idx ? data : p))); }
      else { const { data } = await api.post("/student/notebook", body, { headers: H }); setPages((ps) => (idx < ps.length ? ps.map((p, i) => (i === idx ? data : p)) : [...ps, data])); }
      toast.success("Hoja guardada ✏️");
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  const exportPdf = async () => {
    if (!sheetRef.current) return;
    setExporting(true); setSelId(null);
    try {
      await new Promise((r) => setTimeout(r, 120));
      const dataUrl = await toPng(sheetRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const img = new Image(); img.src = dataUrl; await img.decode();
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210, ph = 297, margin = 10;
      const ratio = img.width / img.height;
      let w = pw - margin * 2; let h = w / ratio;
      if (h > ph - margin * 2) { h = ph - margin * 2; w = h * ratio; }
      pdf.addImage(dataUrl, "PNG", (pw - w) / 2, margin, w, h);
      pdf.save(`cuaderno_${(studentName || "alumno").replace(/\s+/g, "_")}_${date}.pdf`);
      toast.success("PDF generado 📄");
    } catch { toast.error("No se pudo generar el PDF"); } finally { setExporting(false); }
  };

  const newPage = () => { setPages((ps) => [...ps, { id: null, date: todayISO(), note: "", overlays: [], sheet: "lines" }]); setIdx(pages.length); };
  const removePage = async () => {
    if (!page) return;
    if (page.id) { try { await api.delete(`/student/notebook/${page.id}`, { headers: H }); } catch { /* noop */ } }
    const np = pages.filter((_, i) => i !== idx); setPages(np); setIdx(Math.max(0, idx - 1)); toast.success("Hoja eliminada");
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-sky-600" /></div>;

  return (
    <div data-testid="student-notebook">
      <style>{`
        .nb-lines{ background-image: repeating-linear-gradient(#ffffff, #ffffff 33px, #cfe3f7 34px); }
        [data-testid="nb-note"]:empty:before{ content: attr(data-placeholder); color:#94a3b8; }
        [data-testid="nb-note"] ul{ list-style: disc; padding-left: 1.4rem; }
      `}</style>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={newPage} data-testid="nb-new" className="text-xs inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white px-3 py-2 rounded-lg font-semibold"><Plus className="w-4 h-4" /> Nueva hoja</button>

        <div className="flex items-center gap-1 bg-white rounded-lg border border-nardo-200 p-1" data-testid="nb-text-toolbar">
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} data-testid="nb-bold" title="Negrita" className="w-8 h-8 rounded-md text-navy hover:bg-nardo-50 flex items-center justify-center"><Bold className="w-4 h-4" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} data-testid="nb-italic" title="Cursiva" className="w-8 h-8 rounded-md text-navy hover:bg-nardo-50 flex items-center justify-center"><Italic className="w-4 h-4" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("underline"); }} data-testid="nb-underline" title="Subrayado" className="w-8 h-8 rounded-md text-navy hover:bg-nardo-50 flex items-center justify-center"><Underline className="w-4 h-4" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} data-testid="nb-list" title="Lista" className="w-8 h-8 rounded-md text-navy hover:bg-nardo-50 flex items-center justify-center"><List className="w-4 h-4" /></button>
          <select onChange={(e) => { if (e.target.value) exec("fontName", e.target.value); e.target.selectedIndex = 0; }} data-testid="nb-font" title="Tipografía" className="h-8 text-xs border border-nardo-200 rounded-md px-1 bg-white text-navy">
            {FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
          </select>
          <select onChange={(e) => { if (e.target.value) exec("fontSize", e.target.value); e.target.selectedIndex = 0; }} data-testid="nb-fontsize" title="Tamaño" className="h-8 text-xs border border-nardo-200 rounded-md px-1 bg-white text-navy">
            <option value="">Tamaño</option>
            {FSIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input type="color" onChange={(e) => exec("foreColor", e.target.value)} data-testid="nb-textcolor" title="Color de texto" className="w-8 h-8 rounded-md border border-nardo-200 p-0.5 cursor-pointer bg-white" />
        </div>

        <button onClick={() => fileRef.current?.click()} data-testid="nb-add-image" className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-nardo-200 text-navy hover:bg-nardo-50"><ImageIcon className="w-4 h-4" /> Imagen</button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="nb-image-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; f && addImage(f); }} />
        <div className="relative">
          <button onClick={() => setShowStickers((s) => !s)} data-testid="nb-stickers-btn" className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-nardo-200 text-navy hover:bg-nardo-50"><Smile className="w-4 h-4" /> Stickers</button>
          {showStickers && (
            <div className="absolute z-30 mt-1 left-0 bg-white border border-nardo-200 rounded-xl shadow-xl p-3 w-72 max-h-80 overflow-y-auto" data-testid="nb-stickers-panel">
              {STICKER_GROUPS.map((g) => (
                <div key={g.name} className="mb-2 last:mb-0">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-nardo-400 mb-1">{g.name}</div>
                  <div className="grid grid-cols-6 gap-1">
                    {g.items.map((s, i) => <button key={`${g.name}-${i}`} onClick={() => addSticker(s)} className="text-2xl hover:bg-nardo-50 rounded-lg w-9 h-9 flex items-center justify-center">{s}</button>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="inline-flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg border border-nardo-200 text-navy">
          <LayoutTemplate className="w-4 h-4 shrink-0" />
          <select value={sheet} onChange={(e) => setSheet(e.target.value)} data-testid="nb-sheet-select" title="Tipo de hoja" className="bg-transparent outline-none text-xs text-navy">
            {Object.entries(SHEETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button onClick={exportPdf} disabled={exporting} data-testid="nb-pdf" className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-nardo-200 text-navy hover:bg-nardo-50 disabled:opacity-50">{exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF</button>
          <button onClick={save} disabled={saving} data-testid="nb-save" className="text-xs inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
          {page && <button onClick={removePage} data-testid="nb-delete" className="text-xs p-2 rounded-lg border border-nardo-200 text-nardo-500 hover:text-red-600 hover:border-red-300"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      {pages.length === 0 ? (
        <div className="py-16 text-center text-nardo-500"><NotebookPen className="w-10 h-10 mx-auto mb-2 text-sky-500" />Tu cuaderno está vacío. Crea tu primera hoja ✏️</div>
      ) : (
        <>
          {/* Hoja (documento) */}
          <div ref={sheetRef} className="relative bg-white rounded-2xl shadow-xl border border-nardo-200 overflow-hidden max-w-3xl mx-auto"
            onPointerDown={(e) => { if (e.target === e.currentTarget) setSelId(null); }} data-testid="nb-sheet">
            {/* Encabezado: fecha + nombre */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 bg-sky-50 border-b border-sky-100">
              <div className="flex items-center gap-2 text-sm">
                <PenLine className="w-4 h-4 text-sky-600" />
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="nb-date" className="bg-transparent text-navy outline-none text-sm" />
              </div>
              <div className="font-display font-bold text-navy text-sm truncate uppercase tracking-wide" data-testid="nb-student-name">{studentName}</div>
            </div>

            {/* Área de escritura con la plantilla elegida */}
            <div className="relative">
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-testid="nb-note"
                onPointerDown={() => setSelId(null)}
                className="w-full min-h-[520px] text-slate-800 outline-none"
                data-placeholder="Escribe aquí tus anotaciones…"
                style={{
                  fontFamily: "Georgia, serif",
                  fontSize: "16px",
                  lineHeight: `${LINE}px`,
                  padding: "10px 26px 40px 26px",
                  ...(SHEETS[sheet]?.css || SHEETS.lines.css),
                  backgroundOrigin: "content-box",
                  backgroundClip: "content-box",
                  backgroundAttachment: "local",
                }}
              />

              {/* Overlays (imágenes y stickers) — arrastra y ajusta libremente */}
              {overlays.map((ov) => {
                const selected = selId === ov.id;
                return (
                  <div
                    key={ov.id}
                    onPointerDown={(e) => startDrag(e, ov)}
                    data-testid={`nb-overlay-${ov.id}`}
                    style={{
                      position: "absolute", left: `${ov.x}%`, top: `${ov.y}%`, width: `${ov.w}%`,
                      touchAction: "none", cursor: "move", userSelect: "none",
                      outline: selected ? "2px solid #0ea5e9" : "none", outlineOffset: 2, zIndex: selected ? 20 : 10,
                    }}
                  >
                    {ov.kind === "image" ? (
                      <img src={`${API}/aula/archivo/${ov.token}`} alt="" crossOrigin="anonymous" draggable={false}
                        style={{ width: "100%", height: "auto", display: "block", borderRadius: 8, pointerEvents: "none" }} />
                    ) : (
                      <div style={{ width: "100%", containerType: "inline-size", lineHeight: 0, pointerEvents: "none" }}>
                        <span style={{ fontSize: "92cqw", lineHeight: 1, display: "block", textAlign: "center" }}>{ov.emoji}</span>
                      </div>
                    )}
                    {selected && (
                      <>
                        <button onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }} onClick={(e) => { e.stopPropagation(); delOverlay(ov.id); }}
                          data-testid="nb-overlay-delete" title="Eliminar"
                          style={{ position: "absolute", top: -12, right: -12, width: 26, height: 26, borderRadius: 9999, background: "#dc2626", color: "#fff", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.4)", cursor: "pointer" }}>
                          <X style={{ width: 14, height: 14 }} />
                        </button>
                        <div onPointerDown={(e) => startResize(e, ov)} data-testid="nb-overlay-resize" title="Ajustar tamaño"
                          style={{ position: "absolute", bottom: -12, right: -12, width: 24, height: 24, borderRadius: 9999, background: "#0ea5e9", color: "#fff", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.4)", cursor: "nwse-resize", touchAction: "none" }}>
                          <Maximize2 style={{ width: 12, height: 12 }} />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-center text-[11px] text-nardo-400 mt-2">Escribe en las líneas · toca una imagen o sticker para moverlo, cambiar su tamaño o eliminarlo</p>

          {/* Navegación entre hojas */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} data-testid="nb-prev" className="p-2.5 rounded-full bg-white border border-nardo-200 text-navy disabled:opacity-30 hover:bg-nardo-50"><ChevronLeft className="w-5 h-5" /></button>
            <span className="text-sm text-nardo-500 font-medium">Hoja {idx + 1} de {pages.length}</span>
            <button onClick={() => setIdx((i) => Math.min(pages.length - 1, i + 1))} disabled={idx >= pages.length - 1} data-testid="nb-next" className="p-2.5 rounded-full bg-white border border-nardo-200 text-navy disabled:opacity-30 hover:bg-nardo-50"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </>
      )}
    </div>
  );
}
